import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Button, Chip, MenuItem, Select, Snackbar, Switch, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip,
} from '@mui/material';

import { useProfile, withAuthorization } from '../Session';
import {
  PROJECT_ROLES, ROLES, addUserProfile, demoPath, setDemo, setProjectMember, setUserDisabled, setUserRole, watchDemos,
  watchProjects, watchUsers,
} from '../../services';
import './styles.css';

// Administration (modèle v2) : rôles des utilisateurs, comptes désactivés, invités par projet, démos.
// Réservée aux administrateurs ; les règles Firestore refusent de toute façon ces écritures aux autres.
function AdminPage() {
  const profile = useProfile();
  const [users, setUsers] = useState(null);
  const [projects, setProjects] = useState(null);
  const [demos, setDemos] = useState(null);
  const [message, setMessage] = useState(null);

  const isAdmin = profile?.role === 'admin';

  useEffect(() => {
    document.title = 'Administration';
    if (!isAdmin) return undefined;
    const fail = err => setMessage({ severity: 'error', text: `Loading failed: ${err.message}` });
    const unwatchUsers = watchUsers(setUsers, fail);
    const unwatchProjects = watchProjects(profile, setProjects, fail);
    const unwatchDemos = watchDemos(setDemos, fail);
    return () => { unwatchUsers(); unwatchProjects(); unwatchDemos(); };
  }, [isAdmin, profile?.uid]);

  // Exécute une écriture et affiche le résultat.
  const run = (promise, success) => promise
    .then(() => success && setMessage({ severity: 'success', text: success }))
    .catch(err => setMessage({ severity: 'error', text: `Failed: ${err.message}` }));

  if (profile === undefined) return <div id="admin-page"><p className="admin-muted">Loading…</p></div>;
  if (!isAdmin) {
    return (
      <div id="admin-page">
        <h1>Administration</h1>
        <Alert severity="warning">This page is for administrators only.</Alert>
      </div>
    );
  }

  return (
    <div id="admin-page">
      <h1>Administration</h1>

      <section className="admin-section">
        <h2>Users</h2>
        <UsersTable users={users} self={profile.uid} run={run} />
        <AddUserForm users={users} run={run} />
      </section>

      <section className="admin-section">
        <h2>Project guests</h2>
        <p className="admin-muted">
          Administrators, contributors and viewers see every project. An "External" account only sees the projects it
          is invited to; a "Contribute" guest can add and edit samples there.
        </p>
        <ProjectMembers projects={projects} users={users} run={run} />
      </section>

      <section className="admin-section">
        <h2>Demos</h2>
        <p className="admin-muted">
          Samples that every active account, external ones included, opens read-only: the displayed modalities and
          their toggles, without the computation channels or the settings. To add one: "Demo" switch in the sample's
          viewer.
        </p>
        <DemoList demos={demos} projects={projects} profile={profile} run={run} />
      </section>

      <Snackbar open={!!message} autoHideDuration={5000} onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {message ? <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert> : <span />}
      </Snackbar>
    </div>
  );
}

function UsersTable({ users, self, run }) {
  if (!users) return <p className="admin-muted">Loading users…</p>;
  return (
    <div className="admin-table-scroll">
    <Table size="small" className="admin-table">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Email</TableCell>
          <TableCell>Role</TableCell>
          <TableCell align="center">Active</TableCell>
          <TableCell>UID</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map(u => {
          const isSelf = u.uid === self;
          return (
            <TableRow key={u.uid} className={u.disabled ? 'admin-disabled' : ''}>
              <TableCell>{u.displayName || '—'}{isSelf && <span className="admin-muted"> (you)</span>}</TableCell>
              <TableCell>{u.email}</TableCell>
              <TableCell>
                <Tooltip title={isSelf ? 'You cannot change your own role' : ''}>
                  <span>
                    <Select size="small" variant="standard" value={u.role || ''} disabled={isSelf}
                      onChange={e => run(setUserRole(u.uid, e.target.value), `Role of ${u.email} updated`)}>
                      {Object.entries(ROLES).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
                    </Select>
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Switch size="small" checked={!u.disabled} disabled={isSelf}
                  onChange={e => run(setUserDisabled(u.uid, !e.target.checked),
                    e.target.checked ? `${u.email} reactivated` : `${u.email} deactivated`)} />
              </TableCell>
              <TableCell className="admin-uid">
                {u.uid}
                {u.uidField && (
                  <Tooltip title={`The profile has a "uid" field (${u.uidField}) that differs from its document ID: it is ignored, you can delete it in the Firestore console.`}>
                    <span className="admin-uid-warning"> · mismatched uid field</span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    </div>
  );
}

const EMPTY_USER = { uid: '', email: '', displayName: '', role: 'viewer' };

function AddUserForm({ users, run }) {
  const [form, setForm] = useState(EMPTY_USER);
  const set = k => e => setForm({ ...form, [k]: e.target.value });
  // UID qui a déjà une fiche : refusé (l'ajout l'écraserait, rôle compris).
  const existing = (users || []).find(u => u.uid === form.uid.trim());
  const valid = form.uid.trim() && /\S+@\S+/.test(form.email) && !existing;
  const submit = e => {
    e.preventDefault();
    run(addUserProfile(form.uid.trim(), { ...form, email: form.email.trim() }), `Profile of ${form.email} created`)
      .then(() => setForm(EMPTY_USER));
  };
  return (
    <form className="admin-add-user" onSubmit={submit}>
      <h3>Add an existing account</h3>
      <p className="admin-muted">
        First create the account in the Firebase console (Authentication &gt; Add user), then paste its UID here to give
        it a role. Direct email invitations will come in a later version.
      </p>
      <div className="admin-form-row">
        <TextField size="small" label="UID" value={form.uid} onChange={set('uid')} required />
        <TextField size="small" label="Email" value={form.email} onChange={set('email')} required />
        <TextField size="small" label="Display name" value={form.displayName} onChange={set('displayName')} />
        <Select size="small" value={form.role} onChange={set('role')}>
          {Object.entries(ROLES).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
        </Select>
        <Button type="submit" variant="contained" disableElevation disabled={!valid}>Add</Button>
      </div>
      {existing && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          This UID already has a profile ({existing.email || existing.displayName || 'no email'}): change its role in the
          table above.
        </Alert>
      )}
    </form>
  );
}

function ProjectMembers({ projects, users, run }) {
  const byUid = useMemo(() => Object.fromEntries((users || []).map(u => [u.uid, u])), [users]);
  if (!projects || !users) return <p className="admin-muted">Loading projects…</p>;
  if (projects.length === 0) return <p className="admin-muted">No projects yet.</p>;
  return (
    <div className="admin-projects">
      {projects.map(p => (
        <div key={p.id} className="admin-project">
          <div className="admin-project-name">{p.name}</div>
          <div className="admin-project-members">
            {Object.entries(p.members || {}).length === 0 && <span className="admin-muted">No guests</span>}
            {Object.entries(p.members || {}).map(([uid, role]) => (
              <Chip key={uid} size="small"
                label={`${byUid[uid]?.displayName || byUid[uid]?.email || uid} · ${PROJECT_ROLES[role] || role}`}
                onDelete={() => run(setProjectMember(p.id, uid, null), 'Guest removed')} />
            ))}
          </div>
          <InviteForm project={p} users={users} run={run} />
        </div>
      ))}
    </div>
  );
}

function DemoList({ demos, projects, profile, run }) {
  if (!demos) return <p className="admin-muted">Loading demos…</p>;
  if (demos.length === 0) return <p className="admin-muted">No demos yet.</p>;
  const projectName = id => projects?.find(p => p.id === id)?.name || id;
  return (
    <div className="admin-projects">
      {demos.map(demo => (
        <div key={demo.id} className="admin-project admin-demo">
          <Link className="admin-project-name" to={demoPath(demo)}>{demo.name}</Link>
          <span className="admin-muted">{projectName(demo.projectId)}</span>
          <Button size="small" onClick={() => run(setDemo(profile, demo.projectId, demo.id, null), `"${demo.name}" is no longer a demo`)}>
            Remove
          </Button>
        </div>
      ))}
    </div>
  );
}

function InviteForm({ project, users, run }) {
  const [uid, setUid] = useState('');
  const [role, setRole] = useState('viewer');
  const candidates = users.filter(u => !(project.members || {})[u.uid] && !u.disabled);
  return (
    <div className="admin-form-row">
      <Select size="small" displayEmpty value={uid} onChange={e => setUid(e.target.value)} sx={{ minWidth: 240 }}>
        <MenuItem value="" disabled>Invite a user…</MenuItem>
        {candidates.map(u => (
          <MenuItem key={u.uid} value={u.uid}>{u.displayName || u.email} ({ROLES[u.role] || u.role})</MenuItem>
        ))}
      </Select>
      <Select size="small" value={role} onChange={e => setRole(e.target.value)}>
        {Object.entries(PROJECT_ROLES).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
      </Select>
      <Button variant="outlined" disabled={!uid}
        onClick={() => run(setProjectMember(project.id, uid, role), 'Guest added').then(() => setUid(''))}>
        Invite
      </Button>
    </div>
  );
}

export default withAuthorization(authUser => !!authUser)(AdminPage);
